import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLocale } from "../i18n";
import { fetchListing } from "../lib/api";
import SEO, { buildBreadcrumb } from "../components/SEO";
import { ArrowLeft, MapPin, Phone, Mail, Globe, Clock, ArrowUpRight } from "lucide-react";

export default function ListingDetail() {
  const { slug } = useParams();
  const { locale, t } = useLocale();
  const [l, setL] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    fetchListing(slug).then(setL).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="max-w-[900px] mx-auto px-6 py-24 text-center"><p className="font-editorial text-4xl">{locale === "ro" ? "Locul nu a fost găsit." : "Listing not found."}</p><Link to="/regions" className="mt-6 inline-block underline">{locale === "ro" ? "Înapoi la destinații" : "Back to destinations"}</Link></div>;
  if (!l) return <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 text-[var(--wr-ink-2)]">{t("common.loading")}</div>;

  const name = l[`name_${locale}`] || l.name_en;
  const desc = l[`full_description_${locale}`] || l.full_description_en || l[`short_description_${locale}`] || l.short_description_en;
  const price = l[`price_text_${locale}`] || l.price_text_en;
  const highlights = (l[`highlights_${locale}`] && l[`highlights_${locale}`].length ? l[`highlights_${locale}`] : l.highlights_en) || [];
  const best = l[`best_time_${locale}`] || l.best_time_en;
  const access = l[`accessibility_notes_${locale}`] || l.accessibility_notes_en;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": l.listing_type === "restaurant" ? "Restaurant" : l.listing_type === "museum" ? "Museum" : l.listing_type === "castle" ? "Landmark" : (l.listing_type === "accommodation" || l.listing_type === "hotel" || l.listing_type === "guesthouse" || l.listing_type === "cabin") ? "LodgingBusiness" : "TouristAttraction",
    name,
    description: l[`short_description_${locale}`] || l.short_description_en || "",
    image: l.hero_image_url,
    address: l.address,
    telephone: l.phone,
    url: `https://wowromania.com/listings/${l.slug}`,
    ...(l.latitude && l.longitude ? { geo: { "@type": "GeoCoordinates", latitude: l.latitude, longitude: l.longitude } } : {}),
  };

  const mapEmbed = (l.latitude && l.longitude)
    ? `https://www.google.com/maps?q=${l.latitude},${l.longitude}&output=embed`
    : null;

  return (
    <article data-testid={`listing-detail-${l.slug}`}>
      <SEO title={name} description={l[`short_description_${locale}`] || l.short_description_en || name} path={`/listings/${l.slug}`} image={l.hero_image_url} type="article"
        jsonLd={[jsonLd, buildBreadcrumb([
          { name: locale === "ro" ? "Acasă" : "Home", path: "/" },
          { name: locale === "ro" ? "Destinații" : "Destinations", path: "/regions" },
          { name: l.region_slug, path: `/regions/${l.region_slug}` },
          { name, path: `/listings/${l.slug}` },
        ])]} />

      {l.hero_image_url && (
        <div className="relative h-[60vh] overflow-hidden">
          <img src={l.hero_image_url} alt={name} className="absolute inset-0 w-full h-full object-cover" fetchPriority="high" decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
          <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-14 text-white">
            <Link to={`/regions/${l.region_slug}`} className="inline-flex items-center gap-2 font-mono-wr text-[11px] uppercase tracking-widest mb-6 hover:text-[var(--wr-red)] transition-colors">
              <ArrowLeft size={14} /> {locale === "ro" ? "Înapoi la regiune" : "Back to region"}
            </Link>
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="font-mono-wr text-[10px] uppercase tracking-widest bg-[var(--wr-red)] text-white px-2 py-1">{l.listing_type}</span>
              {l.featured && <span className="font-mono-wr text-[10px] uppercase tracking-widest bg-white/20 backdrop-blur px-2 py-1">{locale === "ro" ? "Recomandat" : "Featured"}</span>}
              {l.sponsored && <span className="font-mono-wr text-[10px] uppercase tracking-widest border border-white/40 px-2 py-1">{locale === "ro" ? "Sponsorizat · Divulgat" : "Sponsored · Disclosed"}</span>}
            </div>
            <h1 className="font-editorial text-5xl sm:text-7xl leading-[0.95] tracking-tight">{name}</h1>
            {(l.city || l.locality) && <p className="mt-3 font-mono-wr text-sm text-white/85">{[l.locality, l.city, l.region_slug].filter(Boolean).join(" · ")}</p>}
          </div>
        </div>
      )}

      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-10">
          {desc && (
            <section>
              <p className="font-editorial text-2xl leading-relaxed text-[var(--wr-ink)] whitespace-pre-line">{desc}</p>
            </section>
          )}
          {highlights.length > 0 && (
            <section>
              <h2 className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-4">{t("regions.highlights")}</h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {highlights.map((h, i) => <li key={i} className="border-b border-[var(--wr-border)] pb-2 text-[15px] text-[var(--wr-ink)]">{h}</li>)}
              </ul>
            </section>
          )}
          {(l.amenities || []).length > 0 && (
            <section>
              <h2 className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-4">{locale === "ro" ? "FACILITĂȚI" : "AMENITIES"}</h2>
              <ul className="flex flex-wrap gap-2">
                {l.amenities.map((a, i) => <li key={i} className="border border-[var(--wr-border)] px-3 py-1 text-[13px] font-mono-wr">{a}</li>)}
              </ul>
            </section>
          )}
          {(l.gallery_images || []).length > 0 && (
            <section className="grid grid-cols-2 gap-3">
              {l.gallery_images.map((src, i) => <img key={i} src={src} alt="" className="w-full h-56 object-cover" loading="lazy" />)}
            </section>
          )}
          {mapEmbed && (
            <section>
              <h2 className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-4">{locale === "ro" ? "LOCAȚIE" : "LOCATION"}</h2>
              <iframe title="map" src={mapEmbed} className="w-full h-80 border border-[var(--wr-border)]" loading="lazy" />
              {l.google_maps_url && <a href={l.google_maps_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 font-mono-wr text-[11px] uppercase tracking-widest underline">{locale === "ro" ? "Deschide în Google Maps" : "Open in Google Maps"} <ArrowUpRight size={12} /></a>}
            </section>
          )}
        </div>

        <aside className="lg:col-span-4 space-y-6">
          {price && <div className="border border-[var(--wr-border)] p-6"><p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] mb-2">{locale === "ro" ? "Preț" : "Price"}</p><p className="font-editorial text-2xl text-[var(--wr-ink)]">{price}</p></div>}
          <div className="border border-[var(--wr-border)] p-6 space-y-3 text-[14px]">
            {l.address && <p className="flex items-start gap-3"><MapPin size={16} className="text-[var(--wr-red)] mt-1 shrink-0"/><span>{l.address}</span></p>}
            {l.opening_hours && <p className="flex items-start gap-3"><Clock size={16} className="text-[var(--wr-red)] mt-1 shrink-0"/><span>{l.opening_hours}</span></p>}
            {l.phone && <p className="flex items-start gap-3"><Phone size={16} className="text-[var(--wr-red)] mt-1 shrink-0"/><a href={`tel:${l.phone}`} className="underline">{l.phone}</a></p>}
            {l.reservation_email && <p className="flex items-start gap-3"><Mail size={16} className="text-[var(--wr-red)] mt-1 shrink-0"/><a href={`mailto:${l.reservation_email}`} className="underline">{l.reservation_email}</a></p>}
            {l.website_url && <p className="flex items-start gap-3"><Globe size={16} className="text-[var(--wr-red)] mt-1 shrink-0"/><a href={l.website_url} target="_blank" rel="noopener noreferrer" className="underline break-all">{l.website_url}</a></p>}
          </div>
          {(l.check_in || l.check_out) && <div className="border border-[var(--wr-border)] p-6 text-[14px] space-y-2"><p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] mb-1">Check-in / out</p>{l.check_in && <p>Check-in: {l.check_in}</p>}{l.check_out && <p>Check-out: {l.check_out}</p>}</div>}
          {best && <div className="border border-[var(--wr-border)] p-6 text-[14px]"><p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] mb-2">{locale === "ro" ? "Cel mai bun moment" : "Best time to visit"}</p><p>{best}</p></div>}
          {(l.family_friendly || l.pet_friendly) && <div className="border border-[var(--wr-border)] p-6 text-[14px] space-y-2 font-mono-wr">{l.family_friendly && <p>· {locale === "ro" ? "Prietenos cu familia" : "Family-friendly"}</p>}{l.pet_friendly && <p>· {locale === "ro" ? "Prietenos cu animalele" : "Pet-friendly"}</p>}</div>}
          {access && <div className="border border-[var(--wr-border)] p-6 text-[14px]"><p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] mb-2">{locale === "ro" ? "Accesibilitate" : "Accessibility"}</p><p>{access}</p></div>}
          {l.updated_at && <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">{locale === "ro" ? "Actualizat" : "Last updated"} · {l.updated_at.slice(0,10)}</p>}
        </aside>
      </div>
    </article>
  );
}
